import type { DeadlineRule, PackageRegistration, RegistrationSeal, VacancyRegistration } from "./types";

export type SealTone = "ok" | "pending" | "audit" | "noPolicy" | "unknown";

const TONE: Record<RegistrationSeal, SealTone> = {
  OK: "ok",
  PENDING: "pending",
  IN_AUDIT: "audit",
  NO_POLICY: "noPolicy",
  UNKNOWN: "unknown",
};

const LABEL: Record<SealTone, string> = {
  ok: "Registro OK",
  pending: "Registro pendente",
  audit: "Em auditoria",
  noPolicy: "Sem política",
  unknown: "Registro: —",
};

const toneOf = (s: unknown): SealTone => (typeof s === "string" && s in TONE ? TONE[s as RegistrationSeal] : "unknown");

/** Selo de um plantão; status fora do contrato vira "—" (sem inventar estado). */
export function sealView(status: RegistrationSeal | null | undefined): { tone: SealTone; label: string } {
  const tone = toneOf(status);
  return { tone, label: LABEL[tone] };
}

// Pior estado primeiro (§2.2): PENDING > IN_AUDIT > UNKNOWN > NO_POLICY > OK.
const ORDER: RegistrationSeal[] = ["PENDING", "IN_AUDIT", "UNKNOWN", "NO_POLICY", "OK"];

export function worstSeal(list: RegistrationSeal[]): RegistrationSeal | null {
  return ORDER.find((s) => list.includes(s)) ?? null;
}

const isCount = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v) && v >= 0;

/** Agregado do pacote: "3/5 com registro OK" no tom do pior estado (mockup V1, `seloPacote`). */
export function packageSealView(reg: PackageRegistration | null | undefined): { tone: SealTone; label: string } | null {
  if (!reg || !isCount(reg.ok) || !isCount(reg.total) || reg.total === 0) return null;
  const tone = toneOf(reg.worst);
  const ratio = `${reg.ok}/${reg.total}`;
  if (tone === "ok") return { tone, label: `Registro OK · ${ratio}` };
  if (tone === "noPolicy" && reg.ok === 0) return { tone, label: `Sem política · ${reg.total}/${reg.total}` };
  return { tone, label: `${ratio} com registro OK` };
}

const HOUR = 3_600_000;
function span(ms: number): string {
  if (ms >= 24 * HOUR) return `${Math.floor(ms / (24 * HOUR))} d`;
  const hours = Math.floor(ms / HOUR);
  return hours >= 1 ? `${hours} h` : "";
}

/** Prazo de envio do registro do plantão: "vence em 2 d", "vencido há 3 d", "· prazo padrão" (regra DEFAULT). */
export function deadlineLabel(
  reg: Pick<VacancyRegistration, "status" | "deadlineAt"> & { deadlineRule?: DeadlineRule | null; overdue?: boolean; dueSoon?: boolean } | null | undefined,
  now: Date = new Date(),
): string | null {
  if (!reg) return null;
  if (reg.status === "IN_AUDIT") return "em auditoria";
  if (reg.status !== "PENDING" || !reg.deadlineAt) return null;
  const at = Date.parse(reg.deadlineAt);
  if (!Number.isFinite(at)) return null;
  const diff = at - now.getTime();
  const base =
    diff > 0
      ? span(diff)
        ? `vence em ${span(diff)}`
        : "vence em menos de 1 h"
      : span(-diff)
        ? `vencido há ${span(-diff)}`
        : "vencido há menos de 1 h";
  return reg.deadlineRule === "DEFAULT" ? `${base} · prazo padrão` : base;
}
